import React, { useState } from 'react';
import { Award, GraduationCap, ShieldCheck, Globe2, CheckCircle2 } from 'lucide-react';

// Brand palette — Royal Blue + Red
const BRAND_BLUE = '#1E40AF';
const BRAND_RED = '#DC2626';

const CARDS = [
  {
    icon: <Award className="h-8 w-8" />,
    title: 'WHO Recognized',
    tagline: 'Globally Valid Degree',
    stat: '195+',
    statLabel: 'Countries Accept',
    accent: BRAND_BLUE,
    points: ['NMC / MCI Approved', 'Valid in UK, USA, Canada', 'Eligible for USMLE, PLAB'],
  },
  {
    icon: <GraduationCap className="h-8 w-8" />,
    title: 'Affordable Fees',
    tagline: 'Govt. Subsidized Cost',
    stat: '₹25L',
    statLabel: 'Total 6-Year Cost',
    accent: BRAND_RED,
    points: ['No hidden charges', '₹3–5L/year tuition', 'Cheaper than Indian pvt. colleges'],
  },
  {
    icon: <ShieldCheck className="h-8 w-8" />,
    title: 'No Donation',
    tagline: 'Pure Merit Admission',
    stat: '0',
    statLabel: 'Capitation Fee',
    accent: BRAND_BLUE,
    points: ['NEET-based admission', 'Direct university seats', 'No middlemen markup'],
  },
  {
    icon: <Globe2 className="h-8 w-8" />,
    title: 'English Medium',
    tagline: 'Full English Program',
    stat: '6',
    statLabel: 'Years in English',
    accent: BRAND_RED,
    points: ['No language barrier', 'Qualified English faculty', 'FMGE/NExT coaching included'],
  },
];

function WhyCard({ card }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className="relative rounded-2xl overflow-hidden cursor-pointer select-none bg-white border border-border"
      style={{
        transition: 'transform 0.35s cubic-bezier(.22,1,.36,1), box-shadow 0.35s, border-color 0.35s',
        transform: hovered ? 'translateY(-10px)' : 'translateY(0)',
        boxShadow: hovered ? `0 22px 48px -10px ${card.accent}55` : '0 4px 14px 0 rgba(15,23,42,0.06)',
        borderColor: hovered ? `${card.accent}66` : undefined,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Top accent bar — always visible, expands on hover */}
      <div className="absolute top-0 left-0 right-0 h-1.5 rounded-t-2xl"
        style={{
          background: card.accent,
          transform: hovered ? 'scaleX(1)' : 'scaleX(0.35)',
          transformOrigin: 'left',
          transition: 'transform 0.4s cubic-bezier(.22,1,.36,1)',
        }} />

      <div className="relative p-7 flex flex-col h-full">
        {/* Icon tile — filled from start with brand color */}
        <div className="h-14 w-14 rounded-2xl flex items-center justify-center mb-5 shadow-md transition-all duration-300"
          style={{
            background: card.accent,
            color: '#fff',
            boxShadow: hovered ? `0 10px 24px -6px ${card.accent}80` : `0 4px 12px -4px ${card.accent}55`,
          }}>
          {card.icon}
        </div>

        <h4 className="font-serif text-xl font-bold text-primary mb-1">{card.title}</h4>
        <p className="text-sm text-muted-foreground mb-5">{card.tagline}</p>

        <div className="mb-5 flex items-baseline gap-2">
          <span className="font-serif text-4xl font-extrabold" style={{ color: card.accent }}>{card.stat}</span>
          <span className="text-sm text-muted-foreground">{card.statLabel}</span>
        </div>

        <div style={{
          maxHeight: hovered ? '160px' : '0px',
          opacity: hovered ? 1 : 0,
          overflow: 'hidden',
          transition: 'max-height 0.4s cubic-bezier(.22,1,.36,1), opacity 0.3s',
        }}>
          <ul className="space-y-1.5">
            {card.points.map((pt, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                <CheckCircle2 className="h-4 w-4 flex-shrink-0" style={{ color: card.accent }} />
                {pt}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function WhyRussia() {
  return (
    <section id="mbbs" className="py-24 bg-white">
      <div className="container mx-auto max-w-7xl px-4 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <h2 className="text-sm font-bold text-brand uppercase tracking-widest mb-2">The Russian Advantage</h2>
          <h3 className="font-serif text-4xl md:text-5xl font-bold text-primary mb-4">Why Study MBBS in Russia?</h3>
          <div className="mx-auto h-1 w-20 rounded-full bg-brand mb-5" />
          <p className="text-lg text-muted-foreground">Russia offers a perfect blend of high-quality medical education, affordable fees, and global recognition, making it the top destination for Indian medical aspirants.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {CARDS.map((card, i) => <WhyCard key={i} card={card} />)}
        </div>
      </div>
    </section>
  );
}
